// File: src/pages/WelcomePage.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import paymentService from '../services/paymentService';
import { SubscriptionPlan, BillingIntervalEnum } from '../types';
import Spinner from '../components/common/Spinner';
import SubscriptionPlanCard from '../features/subscriptions/components/SubscriptionPlanCard';
import Button from '../components/common/Button';
import logoSrc from '../assets/icons/Logo512.png';
import {
    SparklesIcon, ChevronDownIcon, ClockIcon, BookOpenIcon, ArrowTrendingUpIcon,
    LightBulbIcon, UserGroupIcon, RocketLaunchIcon, LockClosedIcon
} from '@heroicons/react/24/solid';

// --- Reusable Components for the Landing Page ---

const HeroSection = () => (
    <div className="relative isolate overflow-hidden bg-gray-900 py-24 sm:py-32">
        {/* Background image with a subtle overlay for text readability */}
        <div className="absolute inset-0 -z-10">
            <img
                src="https://images.unsplash.com/photo-1521737604893-d14cc237f11d?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2084&q=80"
                alt="Coaching session background"
                className="h-full w-full object-cover object-right md:object-center"
            />
            <div className="absolute inset-0 bg-gray-900 opacity-60"></div> {/* Dark overlay */}
        </div>
        
        <div className="mx-auto max-w-7xl px-6 lg:px-8 relative z-10">
            {/* The div below now consistently uses mx-auto and text-center */}
            <div className="mx-auto max-w-2xl text-center">
                <img src={logoSrc} alt="LifeLine App Logo" className="h-28 w-28 mx-auto mb-8 drop-shadow-lg" />
                <h1 className="text-5xl font-extrabold tracking-tight text-white sm:text-7xl leading-tight">
                    Elevate Your Coaching Impact
                </h1>
                <p className="mt-8 text-xl leading-relaxed text-gray-300 max-w-2xl mx-auto">
                    From client management to progress tracking, LifeLine provides the all-in-one platform you need to deliver transformative results and scale your business.
                </p>
                <div className="mt-12 flex items-center justify-center gap-x-6">
                    <a href="#pricing">
                        <Button size="lg" variant="primary" className="shadow-xl hover:shadow-2xl">Start Your Free Trial</Button>
                    </a>
                    <Link to="/login" className="text-lg font-semibold leading-6 text-white hover:text-primary-light transition-colors">
                        Log in <span aria-hidden="true">→</span>
                    </Link>
                </div>
            </div>
        </div>
    </div>
);

const WhyLifeLineSection = () => {
    const reasons = [
        {
            name: 'Save Time & Automate',
            description: 'Streamline administrative tasks, automate reminders, and focus more on coaching.',
            icon: ClockIcon,
        },
        {
            name: 'Boost Client Engagement',
            description: 'Interactive tools and progress tracking keep clients motivated and accountable.',
            icon: UserGroupIcon,
        },
        {
            name: 'Deliver Deeper Insights',
            description: 'Leverage powerful frameworks and AI analysis for profound client transformations.',
            icon: LightBulbIcon,
        },
        {
            name: 'Scale Your Practice',
            description: 'Manage more clients efficiently without compromising on personalized support.',
            icon: RocketLaunchIcon,
        },
    ];

    return (
        <div className="bg-neutral-lightest py-24 sm:py-32">
            <div className="mx-auto max-w-7xl px-6 lg:px-8">
                <div className="mx-auto max-w-3xl text-center">
                    <h2 className="text-base font-semibold leading-7 text-primary">Why LifeLine?</h2>
                    <p className="mt-2 text-4xl font-bold tracking-tight text-gray-900 sm:text-5xl">
                        Empower Your Coaching Practice
                    </p>
                    <p className="mt-6 text-lg leading-8 text-neutral-dark">
                        LifeLine is built by coaches, for coaches. We understand your needs and provide the tools to elevate your impact.
                    </p>
                </div>
                <div className="mx-auto mt-16 max-w-2xl sm:mt-20 lg:mt-24 lg:max-w-none">
                    <dl className="grid grid-cols-1 gap-x-8 gap-y-16 md:grid-cols-2 lg:grid-cols-4">
                        {reasons.map((reason) => (
                            <div key={reason.name} className="flex flex-col items-center text-center p-4">
                                <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary-lightest text-primary-dark shadow-md mb-4">
                                    <reason.icon className="h-8 w-8" aria-hidden="true" />
                                </div>
                                <dt className="text-xl font-semibold leading-8 text-gray-900">
                                    {reason.name}
                                </dt>
                                <dd className="mt-2 text-base leading-7 text-gray-600">{reason.description}</dd>
                            </div>
                        ))}
                    </dl>
                </div>
            </div>
        </div>
    );
};

const HowItWorksSection = () => {
    const steps = [
        {
            number: 1,
            title: 'Sign Up & Onboard',
            description: 'Create your free trial account and set up your personalized coaching profile in minutes.',
            icon: LockClosedIcon, // Placeholder icon
        },
        {
            number: 2,
            title: 'Invite Clients',
            description: 'Easily send invitations to your clients to join your private coaching space on LifeLine.',
            icon: UserGroupIcon, // Placeholder icon
        },
        {
            number: 3,
            title: 'Coach & Track Progress',
            description: 'Utilize integrated tools for goal setting, habit tracking, journaling, and secure messaging.',
            icon: ArrowTrendingUpIcon, // Placeholder icon
        },
    ];

    return (
        <div className="bg-white py-24 sm:py-32">
            <div className="mx-auto max-w-7xl px-6 lg:px-8">
                <div className="mx-auto max-w-3xl text-center">
                    <h2 className="text-base font-semibold leading-7 text-primary">Simple Steps</h2>
                    <p className="mt-2 text-4xl font-bold tracking-tight text-gray-900 sm:text-5xl">
                        Your Coaching Journey, Simplified
                    </p>
                </div>
                <div className="mt-16 grid grid-cols-1 gap-y-12 sm:grid-cols-2 lg:grid-cols-3 lg:gap-x-8 lg:gap-y-16">
                    {steps.map((step) => (
                        <div key={step.number} className="flex flex-col items-center text-center p-6 bg-neutral-lightest rounded-lg shadow-md">
                            <div className="text-5xl font-extrabold text-primary-dark mb-4">{step.number}</div>
                            <h3 className="text-2xl font-semibold text-gray-900 mb-2">{step.title}</h3>
                            <p className="text-base leading-7 text-gray-600">{step.description}</p>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};


const FeaturesSection = () => {
    const features = [
        {
            name: 'Client Planner',
            description: 'Collaboratively manage goals, habits, and tasks. View your client\'s daily progress in one organized space.',
            icon: ClockIcon,
        },
        {
            name: 'Values & Life Balance Frameworks',
            description: 'Utilize powerful frameworks like the Wheel of Life and Core Values Journey, with more tools coming soon, to provide deeper insights.',
            icon: ArrowTrendingUpIcon,
        },
        {
            name: 'Secure Messaging',
            description: 'Communicate securely with your clients through integrated one-on-one messaging.',
            icon: BookOpenIcon,
        },
        {
            name: 'AI-Powered Insights',
            description: 'Leverage advanced AI capabilities for insightful summaries, personalized guidance, and highlighting key patterns in client activity.',
            icon: SparklesIcon,
        },
    ];

    return (
        <div className="bg-white py-24 sm:py-32">
            <div className="mx-auto max-w-7xl px-6 lg:px-8">
                <div className="mx-auto max-w-2xl lg:text-center">
                    <h2 className="text-base font-semibold leading-7 text-primary">Comprehensive Tools</h2>
                    <p className="mt-2 text-4xl font-bold tracking-tight text-gray-900 sm:text-5xl">
                        Everything you need to succeed
                    </p>
                    <p className="mt-6 text-lg leading-8 text-neutral-dark">
                        LifeLine provides a robust suite of tools designed to enhance every aspect of your coaching.
                    </p>
                </div>
                <div className="mx-auto mt-16 max-w-2xl sm:mt-20 lg:mt-24 lg:max-w-none">
                    <dl className="grid grid-cols-1 gap-x-8 gap-y-16 md:grid-cols-2 lg:grid-cols-2">
                        {features.map((feature) => (
                            <div key={feature.name} className="relative pl-16">
                                <dt className="text-2xl font-semibold leading-9 text-gray-900">
                                    <div className="absolute left-0 top-0 flex h-12 w-12 items-center justify-center rounded-lg bg-primary shadow-md">
                                        <feature.icon className="h-7 w-7 text-white" aria-hidden="true" />
                                    </div>
                                    {feature.name}
                                </dt>
                                <dd className="mt-2 text-base leading-7 text-gray-600">{feature.description}</dd>
                            </div>
                        ))}
                    </dl>
                </div>
            </div>
        </div>
    );
};

const TestimonialsSection = () => {
    const testimonials = [
        {
            body: 'LifeLine has revolutionized how I manage my client relationships. The planner and habit trackers keep my clients engaged between sessions, and the data insights are invaluable.',
            author: { name: 'Sarah J.', handle: 'Executive Coach' },
        },
        {
            body: 'The Wheel of Life and Core Values modules are game-changers. They provide a structured way to facilitate deep, meaningful conversations from day one.',
            author: { name: 'Michael B.', handle: 'Life Balance Coach' },
        },
        {
            body: 'I was hesitant to adopt a new platform, but LifeLine is intuitive, secure, and has genuinely saved me hours of administrative work each week. Highly recommended!',
            author: { name: 'Dr. Emily R.', handle: 'Wellness & Mindset Coach' },
        },
    ];

    return (
        <div className="relative isolate bg-neutral-lightest py-24 sm:py-32">
            <div className="mx-auto max-w-7xl px-6 lg:px-8">
                <div className="mx-auto max-w-xl text-center">
                    <h2 className="text-lg font-semibold leading-8 tracking-tight text-primary">What Coaches Say</h2>
                    <p className="mt-2 text-4xl font-bold tracking-tight text-gray-900 sm:text-5xl">
                        Trusted by coaches worldwide
                    </p>
                </div>
                <div className="mx-auto mt-16 flow-root max-w-2xl sm:mt-20 lg:mx-0 lg:max-w-none">
                    <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
                        {testimonials.map((testimonial) => (
                            <div key={testimonial.author.handle} className="break-inside-avoid pt-8 sm:pt-0">
                                <figure className="rounded-2xl bg-white p-8 text-sm leading-6 shadow-xl ring-1 ring-gray-900/5">
                                    <blockquote className="text-gray-900">
                                        <p className="text-lg leading-7">{`“${testimonial.body}”`}</p>
                                    </blockquote>
                                    <figcaption className="mt-6 flex items-center gap-x-4">
                                        <div className="font-semibold text-gray-900">{testimonial.author.name}</div>
                                        <div className="text-gray-600">{`@${testimonial.author.handle}`}</div>
                                    </figcaption>
                                </figure>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

const FaqSection = () => {
    const faqs = [
        {
            question: "Is there a free trial available?",
            answer: "Yes! All new coaches can sign up for a 7-day free trial to explore all features of the platform. No credit card is required to start your trial.",
        },
        {
            question: "Is my client's data secure?",
            answer: "Absolutely. We prioritize data security and privacy. All data is encrypted in transit and at rest, and our infrastructure follows industry best practices to ensure your and your clients' information is protected.",
        },
        {
            question: "Can I cancel my subscription at any time?",
            answer: "Yes, you can cancel your subscription at any time through the billing portal. You will retain access to your account until the end of your current billing period.",
        },
        {
            question: "What kind of support do you offer?",
            answer: "We offer comprehensive online support, including a knowledge base, tutorials, and direct email support to ensure you get the most out of LifeLine.",
        },
    ];

    return (
        <div className="bg-white">
            <div className="mx-auto max-w-7xl px-6 py-24 sm:py-32 lg:px-8">
                <div className="mx-auto max-w-4xl divide-y divide-gray-900/10">
                    <h2 className="text-3xl font-bold leading-10 tracking-tight text-gray-900 text-center">Frequently Asked Questions</h2>
                    <dl className="mt-10 space-y-6 divide-y divide-gray-900/10">
                        {faqs.map((faq) => (
                            <details key={faq.question} className="pt-6 group">
                                <summary className="flex w-full items-start justify-between text-left text-gray-900 cursor-pointer">
                                    <span className="text-lg font-semibold leading-7">{faq.question}</span>
                                    <span className="ml-6 flex h-7 items-center">
                                        <ChevronDownIcon className="h-6 w-6 group-open:-rotate-180 transition-transform duration-300" />
                                    </span>
                                </summary>
                                <div className="mt-2 pr-12">
                                    <p className="text-base leading-7 text-gray-600">{faq.answer}</p>
                                </div>
                            </details>
                        ))}
                    </dl>
                </div>
            </div>
        </div>
    );
};


const CtaSection = () => (
    <div className="bg-neutral-darkest">
        <div className="mx-auto max-w-7xl px-6 py-24 sm:py-32 lg:px-8 text-center">
            <h2 className="text-4xl font-bold tracking-tight text-white sm:text-5xl leading-tight">
                Ready to transform your coaching practice?
                <br />
                Start your free trial today.
            </h2>
            <div className="mt-12 flex items-center justify-center gap-x-6">
                <a href="#pricing">
                    <Button size="lg" variant="primary" className="shadow-xl hover:shadow-2xl">Get started</Button>
                </a>
                <Link to="/coaches" className="text-lg font-semibold leading-6 text-white hover:text-primary-light transition-colors">
                    Browse Coaches <span aria-hidden="true">→</span>
                </Link>
            </div>
        </div>
    </div>
);


const PricingSection: React.FC<{
    plans: SubscriptionPlan[];
    isLoading: boolean;
    onSelectPlan: (plan: SubscriptionPlan, interval: BillingIntervalEnum) => void;
}> = ({ plans, isLoading, onSelectPlan }) => (
    <div id="pricing" className="py-24 sm:py-32 bg-white">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
            <div className="mx-auto max-w-2xl sm:text-center">
                <h2 className="text-base font-semibold leading-7 text-primary">Affordable & Flexible</h2>
                <p className="mt-2 text-4xl font-bold tracking-tight text-neutral-darkest sm:text-5xl">Simple, Transparent Pricing</p>
                <p className="mt-6 text-lg leading-8 text-neutral-dark">
                    Choose the plan that's right for you. Start with a 7-day free trial, no credit card required.
                </p>
            </div>
            {isLoading ? (
                <div className="flex justify-center mt-16"><Spinner size="lg" /></div>
            ) : (
                <div className="mx-auto mt-16 grid max-w-lg grid-cols-1 items-stretch gap-6 sm:mt-20 lg:max-w-4xl lg:grid-cols-2">
                   {plans.map((plan) => (
                       <div key={plan.id} className="relative">
                           <SubscriptionPlanCard
                               plan={plan}
                               onSelectPlan={onSelectPlan}
                               currentSubscription={null}
                               isLoading={false}
                               processingId={null}
                           />
                       </div>
                   ))}
                </div>
            )}
        </div>
    </div>
);


// --- Main Welcome Page Component ---

const WelcomePage: React.FC = () => {
    const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const navigate = useNavigate();

    const fetchPublicPlans = useCallback(async () => {
        setIsLoading(true);
        try {
            const publicPlans = await paymentService.getPublicPlans();
            setPlans(publicPlans.filter(p => p.is_publicly_visible).sort((a,b) => (a.price_monthly as number || 0) - (b.price_monthly as number || 0)));
        } catch (error) {
            toast.error("Could not load subscription plans. Please try again later.");
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchPublicPlans();
    }, [fetchPublicPlans]);

    const handleSelectPlan = (plan: SubscriptionPlan, interval: BillingIntervalEnum) => {
        toast.success(`You've selected the ${plan.name} (${interval}) plan! Please register to continue.`);
        navigate('/register/coach', { state: { selectedPlanId: plan.id, selectedInterval: interval } });
    };

    return (
        <div className="bg-white">
            <HeroSection />
            <WhyLifeLineSection />
            <HowItWorksSection />
            <FeaturesSection />
            <TestimonialsSection />
            <PricingSection plans={plans} isLoading={isLoading} onSelectPlan={handleSelectPlan} />
            <FaqSection />
            <CtaSection />
        </div>
    );
};

export default WelcomePage;